import { FormEvent, FormEventHandler, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HelperText, Icon, TextField } from '@vst/beam';
import { Visibility, VisibilityOff } from '@vst/beam-icons/icons';

import styles from './taxIdInput.module.scss';

type TaxIdInputProps = {
  regex: RegExp;
  validation: { invalid: string };
  inputValue?: string;
  isRequired: boolean;
  placeholder: string;
  hideStar: boolean;
  onChange?: FormEventHandler<HTMLInputElement>;
};

type VisibilityToggleIconProps = {
  showInputValue: boolean;
  toggleVisibility: () => void;
  error: boolean;
};

const VisibilityToggleIcon = ({ showInputValue, toggleVisibility, error }: VisibilityToggleIconProps) => (
  <div className={styles['visibilityIcon']}>
    <Icon
      color={error ? 'alert_400' : 'inherit'}
      icon={showInputValue ? Visibility : VisibilityOff}
      data-testid="VisibilityIcon"
      onClick={toggleVisibility}
    />
  </div>
);

const validateInput = (inputValue: string | undefined, regex: RegExp, setError: (error: boolean) => void) => {
  if (inputValue) {
    if (!regex.test(inputValue)) {
      setError(true);
      return;
    }
    setError(false);
    return;
  }
  setError(false);
};

export const TaxIdInput = (props: TaxIdInputProps) => {
  const { t } = useTranslation('creditCheck');

  const { inputValue, isRequired, placeholder, regex, validation, hideStar, onChange } = props;
  const [showInputValue, setShowInputValue] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  const handleOnChange = (event: FormEvent<HTMLInputElement>) => {
    event.currentTarget.value = event.currentTarget.value.toUpperCase();
    onChange?.(event);
  };

  const toggleVisibility = () => {
    setShowInputValue(!showInputValue);
  };

  useEffect(() => {
    validateInput(inputValue, regex, setError);
  }, [inputValue, regex, setError]);

  const labelText = isRequired
    ? t('creditCheck.taxId.label')
    : t('creditCheck.taxId.label') + t('creditCheck.taxId.optionalLabel');

  return (
    <>
      {/* This is a dummy input field to prevent autofill from filling the taxId field */}
      <input type="text" name="dummy-username" autoComplete="username" style={{ display: 'none' }} />

      <TextField
        name="taxId"
        fluid={true}
        labelProps={{ labelText, showStar: !hideStar }}
        placeholder={placeholder}
        required={isRequired}
        maxLength={4}
        value={showInputValue ? inputValue : inputValue?.replace(/./g, '•')}
        onChange={handleOnChange}
        data-testid="TaxIdInput"
        autoComplete={'off'}
        contentRight={
          <VisibilityToggleIcon showInputValue={showInputValue} toggleVisibility={toggleVisibility} error={error} />
        }
        className={error ? styles['taxIdErrorInput'] : ''}
      />
      {error && (
        <div className="mt-8">
          <HelperText state="error" helpMessage={validation?.invalid ?? ''} />
        </div>
      )}
    </>
  );
};
